import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, tokenStore } from '../api/client';
import { Alert } from '../components/common';
import { useAuth } from '../state/AuthContext';

/**
 * "I've forgotten my password."
 *
 * The answer is the same whether or not the address has an account, matching
 * the API — a different message here would leak exactly what the API is careful
 * not to.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const response = await api.forgotPassword(email.trim());
      setSent(response.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <section className="page page--narrow center">
        <h1>Check your email</h1>
        <Alert tone="success">{sent}</Alert>
        <p className="muted">The link works once and expires in an hour.</p>
        <Link to="/login" className="btn btn--ghost">
          Back to sign in
        </Link>
      </section>
    );
  }

  return (
    <section className="page page--narrow">
      <h1>Forgot your password?</h1>
      <p className="muted">
        Enter the email address on your account and we&apos;ll send you a link to set a new password.
      </p>

      {error && <Alert>{error}</Alert>}

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Sending…' : 'Send me a link'}
        </button>
      </form>

      <p className="center muted">
        <Link to="/login">Back to sign in</Link>
      </p>
    </section>
  );
}

/** Where the emailed link lands. The token arrives as a query parameter. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { adoptSession } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const auth = await api.resetPassword(token!, password);

      // The API signs them in as part of the reset, so the journey ends on the
      // menu rather than back at a login form.
      tokenStore.save(auth);
      adoptSession(auth.customer);
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setFieldErrors(e.fieldErrors ?? {});
      } else {
        setError((e as Error).message);
      }

      setBusy(false);
    }
  }

  if (!token) {
    return (
      <section className="page page--narrow center">
        <h1>That link is incomplete</h1>
        <Alert>The reset link is missing its token. Ask for a new one.</Alert>
        <Link to="/forgot-password" className="btn btn--primary">
          Send a new link
        </Link>
      </section>
    );
  }

  return (
    <section className="page page--narrow">
      <h1>Choose a new password</h1>
      <p className="muted">At least 8 characters, with a letter and a number.</p>

      {error && (
        <Alert>
          {error}
          {error.includes('expired') || error.includes('not valid') ? (
            <>
              {' '}
              <Link to="/forgot-password">Send a new link</Link>.
            </>
          ) : null}
        </Alert>
      )}

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {fieldErrors.newPassword?.length ? (
            <span className="field__error">{fieldErrors.newPassword.join(' ')}</span>
          ) : null}
        </label>

        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Saving…' : 'Set my new password'}
        </button>
      </form>

      <p className="muted small center">
        Setting a new password signs you out everywhere else.
      </p>
    </section>
  );
}

/** Changing your password while signed in, from the account screen. */
export function ChangePasswordPage() {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (password !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      await api.changePassword(current, password);
      setDone(true);
      setCurrent('');
      setPassword('');
      setConfirm('');
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setFieldErrors(e.fieldErrors ?? {});
      } else {
        setError((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page page--narrow">
      <h1>Change your password</h1>

      {error && <Alert>{error}</Alert>}
      {done && (
        <Alert tone="success">
          Password changed. Every other device you were signed in on has been signed out.
        </Alert>
      )}

      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>

        <label className="field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="muted small">At least 8 characters, with a letter and a number.</span>
          {fieldErrors.newPassword?.length ? (
            <span className="field__error">{fieldErrors.newPassword.join(' ')}</span>
          ) : null}
        </label>

        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
      </form>

      <p className="center muted">
        <Link to="/loyalty">Back to my account</Link>
      </p>
    </section>
  );
}
